// /search — global artifact + repo search results.
//
// The topbar `<SearchBar>` powers an instant popover for the top-20
// fastest matches, but operators eventually want to see all hits for
// a query plus filter by format / repo / kind. That's this page.
//
// State model:
//   * `q`, `format`, `repo`, `kind` live in the URL query string so
//     a search is link-shareable and browser-back works.
//   * `usePagedSearch` owns cursor pagination — the page just calls
//     `loadMore()` for the "Show more" button.
//   * The search input is a controlled component synced to the URL
//     param via `useSearchParams`.
//
// Result table columns:
//   Type · Format · Title · Project / Repo · Version · Size · Pulled
//   Each row navigates to the real detail page for its hit (artifact
//   or repo); "Open →" is a secondary affordance pointing at the same
//   target.
//
// Empty / loading / no-results / error are distinguishable.

import { useEffect, useMemo, useState, type FormEvent, type KeyboardEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search as SearchIcon,
  Box,
  Package,
  AlertTriangle,
  ChevronRight,
  X,
} from 'lucide-react';
import {
  usePagedSearch,
  type PagedSearchFilters,
  type SearchHit,
} from '../api/search';
import { useRepos } from '../api/client';
import { FORMATS } from '../lib/formats';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Skeleton } from '../components/ui/Skeleton';
import { Spinner } from '../components/ui/Spinner';
import { cn } from '../lib/cn';

const KINDS = ['', 'local', 'remote', 'virtual'] as const;
type KindFilter = (typeof KINDS)[number];

const selectClass =
  'h-9 px-3 rounded-md bg-bg-inset text-fg border border-border focus:border-accent focus:bg-bg-elevated transition-colors text-sm';

function fmtBytes(n?: number): string {
  if (!n || n <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function fmtDate(s?: string): string {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
}

function formatTone(format?: string): 'neutral' | 'accent' | 'nebula' | 'warning' {
  switch (format) {
    case 'maven':
    case 'gradle':
      return 'warning';
    case 'docker':
    case 'helm':
      return 'nebula';
    case 'npm':
    case 'pypi':
      return 'accent';
    default:
      return 'neutral';
  }
}

function kindTone(kind?: string): 'neutral' | 'accent' | 'nebula' {
  if (kind === 'remote') return 'nebula';
  if (kind === 'virtual') return 'accent';
  return 'neutral';
}

// Real detail-page target for a hit — artifacts and repos each have
// their own route; the popover and this table must agree on it.
function hitTarget(hit: SearchHit): string {
  return hit.kind === 'artifact'
    ? `/artifacts/${hit.id}`
    : `/repos/${hit.repoId ?? hit.id}`;
}

export function Search() {
  const [params, setParams] = useSearchParams();
  const urlQ = params.get('q') ?? '';
  const urlFormat = params.get('format') ?? '';
  const urlRepo = params.get('repo') ?? '';
  const urlKind = (params.get('kind') ?? '') as KindFilter;

  // Local form state mirrors URL params; only commits via onSubmit
  // so per-keystroke the URL doesn't churn. Hitting Enter or
  // tweaking a filter dropdown commits immediately.
  const [qInput, setQInput] = useState(urlQ);
  useEffect(() => setQInput(urlQ), [urlQ]);

  const filters: PagedSearchFilters = useMemo(
    () => ({
      format: urlFormat || undefined,
      repo: urlRepo || undefined,
      kind: urlKind || '',
    }),
    [urlFormat, urlRepo, urlKind],
  );

  const { hits, isLoading, error, loadMore, nextCursor, pageCount } =
    usePagedSearch(urlQ, filters, 30);

  // Repos for the filter dropdown — lazy-loaded; cheap because the
  // list is already cached by useRepos elsewhere in the app.
  const { data: repos } = useRepos();

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    next.set('q', qInput.trim());
    setParams(next, { replace: false });
  };

  const onChangeFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const onClearFilters = () => {
    const next = new URLSearchParams();
    if (urlQ) next.set('q', urlQ);
    setParams(next, { replace: false });
  };

  const hasFilters = !!(urlFormat || urlRepo || urlKind);

  return (
    <div className="space-y-6">
      <div>
        <p className="orb-eyebrow text-accent/80 mb-2">Discover</p>
        <h1 className="text-2xl font-display font-semibold text-fg leading-tight">
          Global search
        </h1>
        <p className="text-fg-muted text-sm mt-2 max-w-2xl leading-relaxed">
          Search every artifact and repository you have access to. Paste a
          full SHA-256 for an exact match, or type a name fragment for a
          substring lookup. Filter by format, repo, or kind to narrow the
          result set further.
        </p>
      </div>

      <Card>
        <CardHeader>
          <p className="orb-eyebrow text-fg-subtle">Query</p>
        </CardHeader>
        <CardBody>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  'flex items-center flex-1 h-10 px-3 rounded-md border bg-bg-inset',
                  'border-border focus-within:border-accent focus-within:bg-bg-elevated',
                  'transition-colors',
                )}
              >
                <SearchIcon className="h-4 w-4 text-fg-subtle shrink-0" />
                <input
                  value={qInput}
                  onChange={(e) => setQInput(e.target.value)}
                  placeholder="Search packages, paths, sha256…"
                  className="flex-1 bg-transparent outline-none text-sm px-2.5 placeholder:text-fg-subtle"
                  spellCheck={false}
                  autoComplete="off"
                  autoFocus
                />
                {qInput && (
                  <button
                    type="button"
                    aria-label="Clear query"
                    onClick={() => {
                      setQInput('');
                      const next = new URLSearchParams(params);
                      next.delete('q');
                      setParams(next, { replace: false });
                    }}
                    className="h-6 w-6 inline-flex items-center justify-center rounded text-fg-subtle hover:text-fg hover:bg-bg-hover"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <Button type="submit" variant="primary">
                Search
              </Button>
            </div>

            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label htmlFor="search-filter-format" className="orb-eyebrow text-fg-subtle block mb-1">Format</label>
                <select
                  id="search-filter-format"
                  value={urlFormat}
                  onChange={(e) => onChangeFilter('format', e.target.value)}
                  className={selectClass}
                >
                  <option value="">All formats</option>
                  {FORMATS.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="search-filter-kind" className="orb-eyebrow text-fg-subtle block mb-1">Kind</label>
                <select
                  id="search-filter-kind"
                  value={urlKind}
                  onChange={(e) => onChangeFilter('kind', e.target.value)}
                  className={selectClass}
                >
                  <option value="">All kinds</option>
                  {KINDS.filter((k) => !!k).map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
              </div>
              <div className="min-w-[14rem]">
                <label htmlFor="search-filter-repo" className="orb-eyebrow text-fg-subtle block mb-1">Repository</label>
                <select
                  id="search-filter-repo"
                  value={urlRepo}
                  onChange={(e) => onChangeFilter('repo', e.target.value)}
                  className={selectClass + ' w-full'}
                >
                  <option value="">All repositories</option>
                  {(repos ?? []).map((r) => (
                    <option key={r.id} value={r.slug}>
                      {r.slug} ({r.format})
                    </option>
                  ))}
                </select>
              </div>
              {hasFilters && (
                <Button type="button" variant="ghost" onClick={onClearFilters}>
                  Clear filters
                </Button>
              )}
            </div>
          </form>
        </CardBody>
      </Card>

      {/* Results */}
      <SearchResults
        urlQ={urlQ}
        error={error}
        isLoading={isLoading}
        hits={hits}
        hasFilters={hasFilters}
        nextCursor={nextCursor}
        pageCount={pageCount}
        onLoadMore={loadMore}
      />
    </div>
  );
}

interface SearchResultsProps {
  readonly urlQ: string;
  readonly error?: Error;
  readonly isLoading: boolean;
  readonly hits: SearchHit[];
  readonly hasFilters: boolean;
  readonly nextCursor: string;
  readonly pageCount: number;
  readonly onLoadMore: () => void;
}

function SearchResults({
  urlQ,
  error,
  isLoading,
  hits,
  hasFilters,
  nextCursor,
  pageCount,
  onLoadMore,
}: SearchResultsProps) {
  if (urlQ.trim() === '') {
    return (
      <Card>
        <CardBody>
          <div className="py-12 text-center text-sm text-fg-muted">
            <SearchIcon className="h-8 w-8 mx-auto mb-3 text-fg-subtle/60" />
            Type a query above to search across all your artifacts and
            repositories.
          </div>
        </CardBody>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardBody>
          <div className="py-12 text-center text-sm text-warning flex flex-col items-center gap-2">
            <AlertTriangle className="h-6 w-6" />
            {error.message || 'Search failed.'}
          </div>
        </CardBody>
      </Card>
    );
  }

  if (isLoading && hits.length === 0) {
    return (
      <Card>
        <CardHeader>
          <p className="orb-eyebrow text-fg-subtle">Searching…</p>
        </CardHeader>
        <CardBody>
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={`sk-${i}`}
                className="flex items-center gap-4 px-2 py-2 border-b border-border/40 last:border-0"
              >
                <Skeleton width="3rem" height="1.25rem" rounded="9999px" />
                <Skeleton width="3.5rem" height="1.25rem" rounded="9999px" />
                <Skeleton width="40%" />
                <Skeleton width="20%" />
                <Skeleton width="3rem" />
                <Skeleton width="6rem" />
              </div>
            ))}
          </div>
        </CardBody>
      </Card>
    );
  }

  if (hits.length === 0) {
    return (
      <Card>
        <CardBody>
          <div className="py-12 text-center text-sm text-fg-muted">
            No matches for <span className="orb-code">{urlQ}</span>.
            {hasFilters && ' Try clearing the filters.'}
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <ResultsTable
      hits={hits}
      isLoading={isLoading}
      nextCursor={nextCursor}
      pageCount={pageCount}
      onLoadMore={onLoadMore}
    />
  );
}

interface ResultsTableProps {
  readonly hits: SearchHit[];
  readonly isLoading: boolean;
  readonly nextCursor: string;
  readonly pageCount: number;
  readonly onLoadMore: () => void;
}

function ResultsTable({
  hits,
  isLoading,
  nextCursor,
  pageCount,
  onLoadMore,
}: ResultsTableProps) {
  const navigate = useNavigate();

  const onRowKeyDown = (e: KeyboardEvent<HTMLTableRowElement>, hit: SearchHit) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigate(hitTarget(hit));
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <p className="orb-eyebrow text-fg-subtle">
            Results · {hits.length} hit{hits.length === 1 ? '' : 's'}
            {pageCount > 1 ? ` (${pageCount} pages)` : ''}
          </p>
        </div>
      </CardHeader>
      <CardBody>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-fg-subtle text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left font-normal pb-2 pr-3">Type</th>
                <th className="text-left font-normal pb-2 pr-3">Format</th>
                <th className="text-left font-normal pb-2 pr-3">Path / name</th>
                <th className="text-left font-normal pb-2 pr-3">Project / repo</th>
                <th className="text-left font-normal pb-2 pr-3">Version</th>
                <th className="text-left font-normal pb-2 pr-3">Size</th>
                <th className="text-left font-normal pb-2 pr-3">Uploaded</th>
                <th className="text-right font-normal pb-2 pl-3"></th>
              </tr>
            </thead>
            <tbody>
              {hits.map((hit) => (
                <tr
                  key={`${hit.kind}-${hit.id}`}
                  role="link"
                  tabIndex={0}
                  onClick={() => navigate(hitTarget(hit))}
                  onKeyDown={(e) => onRowKeyDown(e, hit)}
                  className="border-t border-border/60 hover:bg-bg-hover/40 cursor-pointer focus:outline-none focus:bg-bg-hover/40"
                >
                  <td className="py-2 pr-3 align-top">
                    {hit.kind === 'repo' ? (
                      <span className="inline-flex items-center gap-1 text-fg-muted">
                        <Box className="h-3.5 w-3.5" /> repo
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-fg-muted">
                        <Package className="h-3.5 w-3.5" /> artifact
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-3 align-top">
                    {hit.format ? (
                      <Badge tone={formatTone(hit.format)}>{hit.format}</Badge>
                    ) : (
                      <span className="text-fg-subtle">—</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 align-top">
                    <div className="text-fg font-medium break-words" title={hit.title}>
                      {hit.title}
                    </div>
                    {hit.path && hit.path !== hit.title && (
                      <div className="text-xs text-fg-muted break-all orb-code">
                        {hit.path}
                      </div>
                    )}
                    {hit.sha256 && (
                      <div
                        className="text-[10px] text-fg-subtle font-mono break-all"
                        title={hit.sha256}
                      >
                        {hit.sha256}
                      </div>
                    )}
                  </td>
                  <td className="py-2 pr-3 align-top">
                    <div className="text-fg">
                      {hit.projectSlug && (
                        <span className="text-fg-muted">{hit.projectSlug} / </span>
                      )}
                      <span>{hit.repoSlug ?? '—'}</span>
                    </div>
                    {hit.repoKind && (
                      <Badge tone={kindTone(hit.repoKind)} className="mt-1">
                        {hit.repoKind}
                      </Badge>
                    )}
                  </td>
                  <td className="py-2 pr-3 align-top text-fg-muted">
                    {hit.version || '—'}
                  </td>
                  <td className="py-2 pr-3 align-top text-fg-muted">
                    {fmtBytes(hit.size)}
                  </td>
                  <td className="py-2 pr-3 align-top text-fg-muted whitespace-nowrap">
                    {fmtDate(hit.uploadedAt)}
                  </td>
                  <td className="py-2 pl-3 align-top text-right">
                    <Link
                      to={hitTarget(hit)}
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 text-accent hover:underline text-xs"
                    >
                      Open
                      <ChevronRight className="h-3.5 w-3.5" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {nextCursor && (
          <div className="mt-4 flex items-center justify-center">
            <Button
              type="button"
              variant="secondary"
              disabled={isLoading}
              onClick={onLoadMore}
            >
              {isLoading ? (
                <span className="inline-flex items-center gap-2">
                  <Spinner size="xs" />
                  Show more
                </span>
              ) : (
                'Show more'
              )}
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
